import type { MouseEvent } from 'react';
import type { ResultsPerPageChangeFn } from '../Pagination/ResultsPerPageDropdown';

import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import { RefreshIcon } from '../../images/shapes';

import Button from '../Button/Button';
import ResultsPerPageDropdown from '../Pagination/ResultsPerPageDropdown';

export interface DataGridColumnOptionsProps {
  colId: string;
  hide?: boolean;
  width?: number;
}

export interface DataGridControlProps {
  /**
   * Reset grid callback
   */
  handleResetGrid: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Rows limit
   */
  limit?: number;
  /**
   * Handle limit rows
   */
  handleLimitChange: ResultsPerPageChangeFn;
  /**
   * Grid options
   */
  gridOptions?: DataGridColumnOptionsProps[];
  /**
   * Callback to update grid options value on visibility change
   */
  setGridOptions: (options: DataGridColumnOptionsProps[]) => void;
  /**
   *  If rows are being deleted
   */
  isDeleting?: boolean;
  /**
   * Test id
   */
  testId?: string;
}

const DataGridControl = ({
  handleResetGrid = EMPTY_FUNCTION,
  limit = 20,
  handleLimitChange,
  isDeleting = false,
  testId = '',
}: DataGridControlProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={twMerge(
        'flex items-center justify-start sm:justify-end w-full sm:w-auto gap-5 ml-auto',
        isDeleting && 'cursor-not-allowed opacity-50',
      )}
    >
      <Button
        iconImage={<RefreshIcon className="text-blue w-3 md:w-4" />}
        onClick={handleResetGrid}
        buttonColor="borderless"
        additionalClasses={twMerge(
          'text-sm md:text-base',
          isDeleting &&
            'bg-transparent hover:bg-transparent active:bg-transparent opacity-50',
        )}
        testId={'button-grid-reset'}
        disabled={isDeleting}
      >
        {t('Global.ResetGrid')}
      </Button>
      <ResultsPerPageDropdown
        value={limit}
        setResultsPerPage={handleLimitChange}
        additionalButtonClasses="pr-8! 2xl:py-1!"
        additionalClasses="p-0! text-sm md:text-base"
        label={t('Global.ResultsPerPage')}
        disabled={isDeleting}
        {...getTestProps(testId, 'results-per-page-dropdown', 'testId')}
      />
    </div>
  );
};

export default DataGridControl;
